// Typed mock data for EPIC-002 (Search Sales Orders), handover run HR-001.
// Draft only — no API-backed source yet. Shapes mirror `sales_order` /
// `sales_order_line` (src/db/schema/handover/) plus the extra NetSuite-style
// fields the source mockup's row detail drawer shows.

import type { EnumOption } from "@/lib/enums";

export interface SalesOrderItem {
  code: string;
  description: string;
}

export interface SalesOrderLocation {
  code: string;
  name: string;
}

export const SALES_ORDER_STATUSES = [
  "Pending Fulfillment",
  "Partially Fulfilled",
  "Backordered",
  "Closed",
] as const;
export type SalesOrderStatus = (typeof SALES_ORDER_STATUSES)[number];

export const SALES_ORDER_STATUS_OPTIONS: readonly EnumOption[] = SALES_ORDER_STATUSES.map(
  (status) => ({ value: status, label: status }),
);

export const SALES_ORDER_ITEMS: readonly SalesOrderItem[] = [
  { code: "JPC-2.0-M", description: "JPC 2.0 Plate Carrier, Medium" },
  { code: "JPC-2.0-L", description: "JPC 2.0 Plate Carrier, Large" },
  { code: "AVS-BASE", description: "AVS Base Vest" },
  { code: "PC-CAGE-M", description: "Precision Cut Cage, Medium" },
  { code: "COMBAT-SHIRT-L", description: "Combat Shirt, Large" },
  { code: "G4-PANT-32", description: "G4 Field Pant, 32" },
];

export const SALES_ORDER_ITEM_OPTIONS: readonly EnumOption[] = SALES_ORDER_ITEMS.map((item) => ({
  value: item.code,
  label: item.code,
}));

export const SALES_ORDER_LOCATIONS: readonly SalesOrderLocation[] = [
  { code: "MAIN-WH", name: "Main Warehouse" },
  { code: "PROD-FL1", name: "Production Floor 1" },
  { code: "PROD-FL2", name: "Production Floor 2" },
  { code: "QC-HOLD", name: "QC Hold" },
];

export const SALES_ORDER_LOCATION_OPTIONS: readonly EnumOption[] = SALES_ORDER_LOCATIONS.map(
  (location) => ({ value: location.code, label: location.name }),
);

export const SALES_ORDER_CUSTOMERS: readonly string[] = [
  "Vanguard Supply",
  "Redline Tactical",
  "Summit Defense Group",
  "Ironclad Logistics",
  "Blackridge Outfitters",
];

export const SALES_ORDER_CUSTOMER_OPTIONS: readonly EnumOption[] = SALES_ORDER_CUSTOMERS.map(
  (customer) => ({ value: customer, label: customer }),
);

export interface SalesOrderLine {
  lineId: string;
  internalId: string;
  customerId: string;
  orderNo: string;
  customer: string;
  status: SalesOrderStatus;
  orderDate: string; // ISO yyyy-mm-dd
  itemCode: string;
  itemDescription: string;
  locationCode: string;
  locationName: string;
  available: number;
  quantity: number;
  quantityBackOrdered: number;
  expectedShipDate: string; // ISO yyyy-mm-dd
  shipComplete: boolean;
  atpLeadTimeDays: number;
  commit: number;
  priceLevel: "Standard" | "Contract";
  rate: number;
  lineAmount: number;
}

const FALLBACK_ITEM: SalesOrderItem = SALES_ORDER_ITEMS[0] ?? {
  code: "JPC-2.0-M",
  description: "JPC 2.0 Plate Carrier, Medium",
};

const FALLBACK_LOCATION: SalesOrderLocation = SALES_ORDER_LOCATIONS[0] ?? {
  code: "MAIN-WH",
  name: "Main Warehouse",
};

function line(
  n: number,
  orderNo: string,
  customer: string,
  status: SalesOrderStatus,
  itemIndex: number,
  locationCode: string,
  available: number,
  quantity: number,
  expectedShipDate: string,
  orderDate: string,
): SalesOrderLine {
  const item = SALES_ORDER_ITEMS[itemIndex % SALES_ORDER_ITEMS.length] ?? FALLBACK_ITEM;
  const location =
    SALES_ORDER_LOCATIONS.find((candidate) => candidate.code === locationCode) ?? FALLBACK_LOCATION;
  const rate = Math.round((62 + itemIndex * 18.5) * 100) / 100;
  const lineAmount = Math.round(rate * quantity * 100) / 100;
  return {
    lineId: `L-${1000 + n}`,
    internalId: String(88000 + n),
    customerId: String(4400 + SALES_ORDER_CUSTOMERS.indexOf(customer)),
    orderNo,
    customer,
    status,
    orderDate,
    itemCode: item.code,
    itemDescription: item.description,
    locationCode: location.code,
    locationName: location.name,
    available,
    quantity,
    quantityBackOrdered: Math.max(0, quantity - available),
    expectedShipDate,
    shipComplete: available >= quantity,
    atpLeadTimeDays: available >= quantity ? 0 : 5 + (itemIndex % 3) * 2,
    commit: Math.min(available, quantity),
    priceLevel: itemIndex % 2 === 0 ? "Standard" : "Contract",
    rate,
    lineAmount,
  };
}

// Fixed sample order lines — stands in for what a real search would pull
// from NetSuite. Same shape and values every load, so the draft behaves
// predictably.
export const SALES_ORDER_LINES: readonly SalesOrderLine[] = [
  line(
    1,
    "SO-10482",
    "Vanguard Supply",
    "Backordered",
    0,
    "MAIN-WH",
    12,
    40,
    "2026-09-28",
    "2026-09-02",
  ),
  line(
    2,
    "SO-10483",
    "Redline Tactical",
    "Pending Fulfillment",
    1,
    "MAIN-WH",
    6,
    6,
    "2026-09-30",
    "2026-09-04",
  ),
  line(
    3,
    "SO-10484",
    "Summit Defense Group",
    "Partially Fulfilled",
    2,
    "PROD-FL1",
    0,
    20,
    "2026-10-05",
    "2026-09-05",
  ),
  line(
    4,
    "SO-10485",
    "Ironclad Logistics",
    "Pending Fulfillment",
    3,
    "PROD-FL2",
    30,
    30,
    "2026-10-01",
    "2026-09-06",
  ),
  line(
    5,
    "SO-10486",
    "Blackridge Outfitters",
    "Backordered",
    4,
    "MAIN-WH",
    4,
    18,
    "2026-10-08",
    "2026-09-06",
  ),
  line(
    6,
    "SO-10487",
    "Vanguard Supply",
    "Pending Fulfillment",
    5,
    "MAIN-WH",
    50,
    50,
    "2026-09-26",
    "2026-09-08",
  ),
  line(7, "SO-10488", "Redline Tactical", "Closed", 0, "QC-HOLD", 0, 8, "2026-09-14", "2026-08-30"),
  line(
    8,
    "SO-10489",
    "Summit Defense Group",
    "Backordered",
    1,
    "MAIN-WH",
    2,
    24,
    "2026-10-11",
    "2026-09-09",
  ),
  line(
    9,
    "SO-10490",
    "Ironclad Logistics",
    "Partially Fulfilled",
    2,
    "PROD-FL1",
    10,
    40,
    "2026-10-03",
    "2026-09-10",
  ),
  line(
    10,
    "SO-10491",
    "Blackridge Outfitters",
    "Pending Fulfillment",
    3,
    "MAIN-WH",
    15,
    15,
    "2026-09-29",
    "2026-09-11",
  ),
  line(
    11,
    "SO-10492",
    "Vanguard Supply",
    "Backordered",
    4,
    "PROD-FL2",
    0,
    12,
    "2026-10-14",
    "2026-09-12",
  ),
  line(
    12,
    "SO-10493",
    "Redline Tactical",
    "Pending Fulfillment",
    5,
    "MAIN-WH",
    22,
    22,
    "2026-09-25",
    "2026-09-12",
  ),
  line(
    13,
    "SO-10494",
    "Summit Defense Group",
    "Partially Fulfilled",
    0,
    "MAIN-WH",
    5,
    30,
    "2026-10-06",
    "2026-09-13",
  ),
  line(
    14,
    "SO-10495",
    "Ironclad Logistics",
    "Backordered",
    1,
    "QC-HOLD",
    0,
    10,
    "2026-10-09",
    "2026-09-13",
  ),
];

export interface SalesOrderSearchFilters {
  orderNo: string;
  customer: string;
  status: string;
  itemCode: string;
  orderDateFrom: string;
  orderDateTo: string;
  shipDateFrom: string;
  shipDateTo: string;
}

export const EMPTY_SEARCH_FILTERS: SalesOrderSearchFilters = {
  orderNo: "",
  customer: "",
  status: "",
  itemCode: "",
  orderDateFrom: "",
  orderDateTo: "",
  shipDateFrom: "",
  shipDateTo: "",
};

export interface SalesOrderSetValues {
  status: string;
  locationCode: string;
  expectedShipDate: string;
}

export const EMPTY_SET_VALUES: SalesOrderSetValues = {
  status: "",
  locationCode: "",
  expectedShipDate: "",
};

export function matchesSearchFilters(
  candidate: SalesOrderLine,
  filters: SalesOrderSearchFilters,
): boolean {
  if (filters.orderNo && !candidate.orderNo.toLowerCase().includes(filters.orderNo.toLowerCase())) {
    return false;
  }
  if (filters.customer && candidate.customer !== filters.customer) return false;
  if (filters.status && candidate.status !== filters.status) return false;
  if (filters.itemCode && candidate.itemCode !== filters.itemCode) return false;
  if (filters.orderDateFrom && candidate.orderDate < filters.orderDateFrom) return false;
  if (filters.orderDateTo && candidate.orderDate > filters.orderDateTo) return false;
  if (filters.shipDateFrom && candidate.expectedShipDate < filters.shipDateFrom) return false;
  if (filters.shipDateTo && candidate.expectedShipDate > filters.shipDateTo) return false;
  return true;
}

export const searchSalesOrdersKeys = {
  all: ["mocks", "search-sales-orders", "lines"] as const,
};

export function fetchSalesOrderLines(): Promise<SalesOrderLine[]> {
  return Promise.resolve([...SALES_ORDER_LINES]);
}

// Formats an ISO yyyy-mm-dd date string (parsed as local midnight, since these
// are date-only values with no time-of-day meaning) for display.
export function formatSalesOrderDate(dateString: string): string {
  if (!dateString) return "—";
  const date = new Date(`${dateString}T00:00:00`);
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
